import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';

const STORAGE_KEY_API_KEY = 'kiosk_api_key';
const STORAGE_KEY_KIOSK_ID = 'kiosk_id';
const STORAGE_KEY_STRIPE_READER_ID = 'stripe_reader_id';
const STORAGE_KEY_STRIPE_PAYMENT_OVERRIDE = 'stripe_payment_override';

// Query-string params stripped from `window.location` after init so credentials
// don't persist in the URL bar or browser history.
const URL_PARAMS = [
  'api_key',
  'key',
  'kiosk_id',
  'stripe_reader_id',
  'reader_id',
  'stripe_payment',
] as const;

function persistString(storageKey: string, value: string | null): void {
  if (value) localStorage.setItem(storageKey, value);
  else localStorage.removeItem(storageKey);
}

class AuthConfig {
  #apiKey: string | null = null;
  #kioskId: string | null = null;
  #stripeReaderId: string | null = null;
  #stripePaymentOverride: boolean | null = null;
  #initialized = false;

  constructor() {
    exposeToGlobalScope(this, 'AuthConfig');
    this.init();
  }

  /**
   * Idempotent. Runs from the constructor on module load; explicit calls
   * from `app.js` are a no-op and kept only for backwards compatibility.
   */
  init(): void {
    if (this.#initialized) return;
    this.#initialized = true;

    const params = new URLSearchParams(window.location.search);

    this.#apiKey = this.#loadStringFromUrl(
      params,
      ['api_key', 'key'],
      STORAGE_KEY_API_KEY
    );
    this.#kioskId = this.#loadStringFromUrl(
      params,
      ['kiosk_id'],
      STORAGE_KEY_KIOSK_ID
    );
    this.#stripeReaderId = this.#loadStringFromUrl(
      params,
      ['stripe_reader_id', 'reader_id'],
      STORAGE_KEY_STRIPE_READER_ID
    );

    const urlOverride = params.get('stripe_payment');
    if (urlOverride !== null) {
      const parsed = urlOverride === 'true' || urlOverride === '1';
      this.#stripePaymentOverride = parsed;
      localStorage.setItem(
        STORAGE_KEY_STRIPE_PAYMENT_OVERRIDE,
        parsed ? 'true' : 'false'
      );
    }

    if (URL_PARAMS.some((p) => params.has(p))) {
      const url = new URL(window.location.href);
      for (const p of URL_PARAMS) url.searchParams.delete(p);
      window.history.replaceState({}, '', url.toString());
    }

    // Fall back to localStorage for any field still empty after URL parse.
    this.#apiKey ??= localStorage.getItem(STORAGE_KEY_API_KEY);
    this.#kioskId ??= localStorage.getItem(STORAGE_KEY_KIOSK_ID);
    this.#stripeReaderId ??= localStorage.getItem(STORAGE_KEY_STRIPE_READER_ID);
    if (this.#stripePaymentOverride === null) {
      const stored = localStorage.getItem(STORAGE_KEY_STRIPE_PAYMENT_OVERRIDE);
      if (stored !== null) this.#stripePaymentOverride = stored === 'true';
    }

    if (!this.#apiKey) {
      console.warn(
        '[AuthConfig] No API key found in URL parameters or localStorage'
      );
    }
  }

  getApiKey(): string | null {
    return this.#apiKey;
  }

  getKioskId(): string | null {
    return this.#kioskId;
  }

  getStripeReaderId(): string | null {
    return this.#stripeReaderId;
  }

  getStripePaymentOverride(): boolean | null {
    return this.#stripePaymentOverride;
  }

  setApiKey(key: string | null): void {
    if (this.#apiKey === key) return;
    this.#apiKey = key;
    persistString(STORAGE_KEY_API_KEY, key);
  }

  setKioskId(id: string | null): void {
    if (this.#kioskId === id) return;
    this.#kioskId = id;
    persistString(STORAGE_KEY_KIOSK_ID, id);
  }

  clear(): void {
    this.#apiKey = null;
    this.#kioskId = null;
    this.#stripeReaderId = null;
    this.#stripePaymentOverride = null;
    localStorage.removeItem(STORAGE_KEY_API_KEY);
    localStorage.removeItem(STORAGE_KEY_KIOSK_ID);
    localStorage.removeItem(STORAGE_KEY_STRIPE_READER_ID);
    localStorage.removeItem(STORAGE_KEY_STRIPE_PAYMENT_OVERRIDE);
  }

  #loadStringFromUrl(
    params: URLSearchParams,
    paramNames: readonly string[],
    storageKey: string
  ): string | null {
    for (const name of paramNames) {
      const value = params.get(name);
      if (value) {
        localStorage.setItem(storageKey, value);
        return value;
      }
    }
    return null;
  }
}

export const authConfig = new AuthConfig();
